// Mock Data Service for BrandZit

export interface Brand {
  id: string
  owner_id: string
  name: string
  slug: string
  description: string | null
  website_url: string | null
  is_public: boolean
  overall_progress: number
  last_activity: string
  created_at: string
  updated_at: string
  metadata?: {
    industry?: string
    target_audience?: string
    brand_personality?: string[]
    primary_goals?: string[]
    ai_analysis?: any
  }
}

export interface Profile {
  id: string
  email: string
  full_name: string | null
  company: string | null
  avatar_url: string | null
  created_at: string
  updated_at: string
}

export interface User {
  id: string
  email: string
  user_metadata?: {
    full_name?: string
    name?: string
    company?: string
    avatar_url?: string
  }
}

// Team collaboration types
export interface TeamMember {
  id: string
  email: string
  name?: string
  avatar?: string
  role: 'owner' | 'admin' | 'editor' | 'viewer'
  joinedAt: string
  lastActive?: string
  status: 'pending' | 'active' | 'inactive'
  invitedBy?: string
}

export interface TeamInvitation {
  id: string
  brandId: string
  email: string
  role: 'admin' | 'editor' | 'viewer'
  invitedBy: string
  invitedAt: string
  expiresAt: string
  status: 'pending' | 'accepted' | 'expired' | 'cancelled'
  token: string
}

export interface ActivityLog {
  id: string
  userId: string
  userName: string
  userAvatar?: string
  brandId: string
  action: string
  details: string
  metadata?: any
  timestamp: string
}

export interface Comment {
  id: string
  userId: string
  userName: string
  userAvatar?: string
  brandId: string
  targetType: 'brand' | 'toolbox_item'
  targetId: string
  content: string
  timestamp: string
  parentId?: string // For replies
  resolved?: boolean
}

// Mock data storage
let mockUser: User | null = null
let mockProfile: Profile | null = null
let mockTeamMembers: Record<string, TeamMember[]> = {} // brandId -> TeamMember[]
let mockInvitations: TeamInvitation[] = []
let mockActivityLogs: ActivityLog[] = []
let mockComments: Comment[] = []
let mockBrands: Brand[] = [
  {
    id: 'brand-1',
    owner_id: 'user-1',
    name: 'TechFlow Solutions',
    slug: 'techflow-solutions-abc123',
    description: 'Innovative software solutions for modern businesses',
    website_url: 'https://techflow.com',
    is_public: true,
    overall_progress: 75,
    last_activity: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days ago
    updated_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
    metadata: {
      industry: 'Technology',
      target_audience: 'SMB and Enterprise',
      brand_personality: ['Innovative', 'Reliable', 'Professional'],
      primary_goals: ['Brand Awareness', 'Lead Generation']
    }
  },
  {
    id: 'brand-2',
    owner_id: 'user-1',
    name: 'Green Garden Co',
    slug: 'green-garden-co-def456',
    description: 'Sustainable gardening solutions and eco-friendly products',
    website_url: 'https://greengarden.co',
    is_public: false,
    overall_progress: 45,
    last_activity: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
    created_at: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days ago
    updated_at: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      industry: 'Agriculture & Environment',
      target_audience: 'Home gardeners and eco-conscious consumers',
      brand_personality: ['Sustainable', 'Natural', 'Educational'],
      primary_goals: ['Community Building', 'Education']
    }
  },
  {
    id: 'brand-3',
    owner_id: 'user-1',
    name: 'Artisan Bakery',
    slug: 'artisan-bakery-ghi789',
    description: 'Handcrafted breads and pastries made with love',
    website_url: null,
    is_public: false,
    overall_progress: 20,
    last_activity: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days ago
    created_at: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(), // 5 days ago
    updated_at: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
    metadata: {
      industry: 'Food & Beverage',
      target_audience: 'Local community and food enthusiasts',
      brand_personality: ['Artisanal', 'Traditional', 'Community-focused'],
      primary_goals: ['Local Recognition', 'Customer Loyalty']
    }
  }
]

let mockStrategyData: Record<string, any> = {}

// Initialize mock team data
const initializeMockTeamData = () => {
  // Add default team members for existing brands
  mockTeamMembers['brand-1'] = [
    {
      id: 'user-1',
      email: 'john.doe@example.com',
      name: 'John Doe',
      role: 'owner',
      joinedAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      lastActive: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString(),
      status: 'active'
    },
    {
      id: 'user-2',
      email: 'sarah.designer@example.com',
      name: 'Sarah Chen',
      role: 'editor',
      joinedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      lastActive: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      status: 'active',
      invitedBy: 'user-1'
    },
    {
      id: 'user-3',
      email: 'mike.marketing@example.com',
      name: 'Mike Rodriguez',
      role: 'viewer',
      joinedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      lastActive: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      status: 'active',
      invitedBy: 'user-1'
    }
  ]

  mockTeamMembers['brand-2'] = [
    {
      id: 'user-1',
      email: 'john.doe@example.com',
      name: 'John Doe',
      role: 'owner',
      joinedAt: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      lastActive: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: 'active'
    }
  ]

  mockTeamMembers['brand-3'] = [
    {
      id: 'user-1',
      email: 'john.doe@example.com',
      name: 'John Doe',
      role: 'owner',
      joinedAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      lastActive: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      status: 'active'
    }
  ]

  // Add some mock activity logs
  mockActivityLogs = [
    {
      id: 'activity-1',
      userId: 'user-2',
      userName: 'Sarah Chen',
      brandId: 'brand-1',
      action: 'updated',
      details: 'Updated color palette in Brand Design Toolbox',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'activity-2',
      userId: 'user-1',
      userName: 'John Doe',
      brandId: 'brand-1',
      action: 'completed',
      details: 'Completed Brand Strategy Canvas',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'activity-3',
      userId: 'user-3',
      userName: 'Mike Rodriguez',
      brandId: 'brand-1',
      action: 'commented',
      details: 'Added comment on Typography System',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'activity-4',
      userId: 'user-2',
      userName: 'Sarah Chen',
      brandId: 'brand-1',
      action: 'uploaded',
      details: 'Uploaded new logo variations',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'activity-5',
      userId: 'user-1',
      userName: 'John Doe',
      brandId: 'brand-1',
      action: 'invited',
      details: 'Invited Mike Rodriguez as a viewer',
      timestamp: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString()
    }
  ]

  // Add some mock comments
  mockComments = [
    {
      id: 'comment-1',
      userId: 'user-3',
      userName: 'Mike Rodriguez',
      brandId: 'brand-1',
      targetType: 'toolbox_item',
      targetId: 'typography',
      content: 'I think we should consider using a more modern sans-serif for the body text. The current choice feels a bit dated.',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString()
    },
    {
      id: 'comment-2',
      userId: 'user-2',
      userName: 'Sarah Chen',
      brandId: 'brand-1',
      targetType: 'toolbox_item',
      targetId: 'typography',
      content: 'Good point! What about Inter or Source Sans Pro? They have great readability.',
      timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000).toISOString(),
      parentId: 'comment-1'
    },
    {
      id: 'comment-3',
      userId: 'user-2',
      userName: 'Sarah Chen',
      brandId: 'brand-1',
      targetType: 'toolbox_item',
      targetId: 'color-palette',
      content: 'Love the new color palette! The blue gradient really captures our tech-forward brand personality.',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString()
    }
  ]
}

// Initialize on load
initializeMockTeamData()

// Mock Auth Service
export const mockAuth = {
  // Sign up a new user
  signUp: async (email: string, password: string, userData?: any): Promise<{ user: User | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API delay
    
    if (email === 'test@error.com') {
      return { user: null, error: 'Invalid email domain' }
    }

    const newUser: User = {
      id: 'user-' + Date.now(),
      email,
      user_metadata: {
        full_name: userData?.full_name || userData?.name || null,
        company: userData?.company || null,
        avatar_url: null
      }
    }

    mockUser = newUser
    
    // Create profile
    mockProfile = {
      id: newUser.id,
      email: newUser.email,
      full_name: newUser.user_metadata?.full_name || null,
      company: newUser.user_metadata?.company || null,
      avatar_url: null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    return { user: newUser, error: null }
  },

  // Sign in existing user
  signIn: async (email: string, password: string): Promise<{ user: User | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API delay
    
    if (password === 'wrongpassword') {
      return { user: null, error: 'Invalid credentials' }
    }

    const user: User = {
      id: 'user-1',
      email,
      user_metadata: {
        full_name: 'John Doe',
        company: 'Demo Company',
        avatar_url: null
      }
    }

    mockUser = user
    
    // Set profile
    mockProfile = {
      id: user.id,
      email: user.email,
      full_name: 'John Doe',
      company: 'Demo Company',
      avatar_url: null,
      created_at: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(), // 30 days ago
      updated_at: new Date().toISOString()
    }

    return { user, error: null }
  },

  // Get current session
  getSession: async (): Promise<{ user: User | null }> => {
    return { user: mockUser }
  },

  // Sign out
  signOut: async (): Promise<void> => {
    mockUser = null
    mockProfile = null
  },

  // Auth state change listener (mock)
  onAuthStateChange: (callback: (event: string, session: { user: User | null } | null) => void) => {
    // Return a mock subscription object
    return {
      data: {
        subscription: {
          unsubscribe: () => {}
        }
      }
    }
  }
}

// Mock Brand Service
export const mockBrandService = {
  // Get all brands for current user
  getBrands: async (): Promise<{ brands: Brand[]; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API delay
    
    if (!mockUser) {
      return { brands: [], error: 'User not authenticated' }
    }

    const userBrands = mockBrands.filter(brand => brand.owner_id === mockUser.id)
    return { brands: userBrands, error: null }
  },

  // Create a new brand
  createBrand: async (brandData: {
    name: string
    description?: string
    website_url?: string
  }): Promise<{ brand: Brand | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 1000)) // Simulate API delay
    
    if (!mockUser) {
      return { brand: null, error: 'User not authenticated' }
    }

    // Calculate initial progress
    let initialProgress = 0
    if (brandData.name) initialProgress += 20
    if (brandData.description) initialProgress += 15
    if (brandData.website_url) initialProgress += 15

    const newBrand: Brand = {
      id: 'brand-' + Date.now(),
      owner_id: mockUser.id,
      name: brandData.name,
      slug: brandData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-') + '-' + Date.now().toString().slice(-6),
      description: brandData.description || null,
      website_url: brandData.website_url || null,
      is_public: false,
      overall_progress: initialProgress,
      last_activity: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }

    mockBrands.push(newBrand)
    return { brand: newBrand, error: null }
  },

  // Update a brand
  updateBrand: async (brandId: string, updates: Partial<Brand>): Promise<{ brand: Brand | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API delay
    
    const brandIndex = mockBrands.findIndex(b => b.id === brandId)
    if (brandIndex === -1) {
      return { brand: null, error: 'Brand not found' }
    }

    const updatedBrand = {
      ...mockBrands[brandIndex],
      ...updates,
      updated_at: new Date().toISOString(),
      last_activity: new Date().toISOString()
    }

    mockBrands[brandIndex] = updatedBrand
    return { brand: updatedBrand, error: null }
  },

  // Delete a brand
  deleteBrand: async (brandId: string): Promise<{ error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API delay
    
    const brandIndex = mockBrands.findIndex(b => b.id === brandId)
    if (brandIndex === -1) {
      return { error: 'Brand not found' }
    }

    mockBrands.splice(brandIndex, 1)
    return { error: null }
  },

  // Get brand by ID
  getBrand: async (brandId: string): Promise<{ brand: Brand | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 300)) // Simulate API delay
    
    const brand = mockBrands.find(b => b.id === brandId)
    if (!brand) {
      return { brand: null, error: 'Brand not found' }
    }

    return { brand, error: null }
  }
}

// Mock Profile Service
export const mockProfileService = {
  // Get current user profile
  getProfile: async (): Promise<{ profile: Profile | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 300)) // Simulate API delay
    
    if (!mockUser) {
      return { profile: null, error: 'User not authenticated' }
    }

    return { profile: mockProfile, error: null }
  },

  // Update user profile
  updateProfile: async (updates: Partial<Profile>): Promise<{ profile: Profile | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API delay
    
    if (!mockProfile) {
      return { profile: null, error: 'Profile not found' }
    }

    mockProfile = {
      ...mockProfile,
      ...updates,
      updated_at: new Date().toISOString()
    }

    return { profile: mockProfile, error: null }
  }
}

// Mock Strategy Service
export const mockStrategyService = {
  // Get brand strategy
  getStrategy: async (brandId: string): Promise<{ strategy: any | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 300)) // Simulate API delay
    
    const strategy = mockStrategyData[brandId] || null
    return { strategy, error: null }
  },

  // Save brand strategy
  saveStrategy: async (brandId: string, strategyData: any): Promise<{ strategy: any; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 500)) // Simulate API delay
    
    mockStrategyData[brandId] = strategyData
    
    // Update brand last activity
    const brandIndex = mockBrands.findIndex(b => b.id === brandId)
    if (brandIndex !== -1) {
      mockBrands[brandIndex] = {
        ...mockBrands[brandIndex],
        last_activity: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    }

    return { strategy: strategyData, error: null }
  }
}

// Helper function to set mock user (for development/testing)
export const setMockUser = (user: User | null) => {
  mockUser = user
}

// Helper function to get current mock user
export const getCurrentMockUser = () => mockUser

// Mock Team Service
export const mockTeamService = {
  // Get team members for a brand
  getTeamMembers: async (brandId: string): Promise<{ members: TeamMember[]; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    const members = mockTeamMembers[brandId] || []
    return { members, error: null }
  },

  // Invite a team member
  inviteTeamMember: async (brandId: string, email: string, role: 'admin' | 'editor' | 'viewer'): Promise<{ invitation: TeamInvitation | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    if (!mockUser) {
      return { invitation: null, error: 'User not authenticated' }
    }

    // Check if user is already a member
    const existingMembers = mockTeamMembers[brandId] || []
    if (existingMembers.some(member => member.email === email)) {
      return { invitation: null, error: 'User is already a team member' }
    }

    // Check for existing pending invitation
    const existingInvitation = mockInvitations.find(
      inv => inv.brandId === brandId && inv.email === email && inv.status === 'pending'
    )
    if (existingInvitation) {
      return { invitation: null, error: 'Invitation already sent' }
    }

    const invitation: TeamInvitation = {
      id: 'invitation-' + Date.now(),
      brandId,
      email,
      role,
      invitedBy: mockUser.id,
      invitedAt: new Date().toISOString(),
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
      status: 'pending',
      token: 'mock-token-' + Date.now()
    }

    mockInvitations.push(invitation)

    // Add activity log
    const activity: ActivityLog = {
      id: 'activity-' + Date.now(),
      userId: mockUser.id,
      userName: mockUser.user_metadata?.full_name || mockUser.email,
      brandId,
      action: 'invited',
      details: `Invited ${email} as ${role}`,
      timestamp: new Date().toISOString()
    }
    mockActivityLogs.push(activity)

    return { invitation, error: null }
  },

  // Remove team member
  removeTeamMember: async (brandId: string, memberId: string): Promise<{ error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    if (!mockUser) {
      return { error: 'User not authenticated' }
    }

    const members = mockTeamMembers[brandId] || []
    const memberIndex = members.findIndex(member => member.id === memberId)
    
    if (memberIndex === -1) {
      return { error: 'Team member not found' }
    }

    const member = members[memberIndex]
    if (member.role === 'owner') {
      return { error: 'Cannot remove brand owner' }
    }

    members.splice(memberIndex, 1)
    mockTeamMembers[brandId] = members

    // Add activity log
    const activity: ActivityLog = {
      id: 'activity-' + Date.now(),
      userId: mockUser.id,
      userName: mockUser.user_metadata?.full_name || mockUser.email,
      brandId,
      action: 'removed',
      details: `Removed ${member.name || member.email} from team`,
      timestamp: new Date().toISOString()
    }
    mockActivityLogs.push(activity)

    return { error: null }
  },

  // Update team member role
  updateTeamMemberRole: async (brandId: string, memberId: string, newRole: 'admin' | 'editor' | 'viewer'): Promise<{ member: TeamMember | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    if (!mockUser) {
      return { member: null, error: 'User not authenticated' }
    }

    const members = mockTeamMembers[brandId] || []
    const memberIndex = members.findIndex(member => member.id === memberId)
    
    if (memberIndex === -1) {
      return { member: null, error: 'Team member not found' }
    }

    const member = members[memberIndex]
    if (member.role === 'owner') {
      return { member: null, error: 'Cannot change owner role' }
    }

    const oldRole = member.role
    member.role = newRole
    members[memberIndex] = member
    mockTeamMembers[brandId] = members

    // Add activity log
    const activity: ActivityLog = {
      id: 'activity-' + Date.now(),
      userId: mockUser.id,
      userName: mockUser.user_metadata?.full_name || mockUser.email,
      brandId,
      action: 'updated',
      details: `Changed ${member.name || member.email}'s role from ${oldRole} to ${newRole}`,
      timestamp: new Date().toISOString()
    }
    mockActivityLogs.push(activity)

    return { member, error: null }
  },

  // Get pending invitations for a brand
  getInvitations: async (brandId: string): Promise<{ invitations: TeamInvitation[]; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const invitations = mockInvitations.filter(inv => inv.brandId === brandId && inv.status === 'pending')
    return { invitations, error: null }
  },

  // Cancel invitation
  cancelInvitation: async (invitationId: string): Promise<{ error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const invitationIndex = mockInvitations.findIndex(inv => inv.id === invitationId)
    if (invitationIndex === -1) {
      return { error: 'Invitation not found' }
    }

    mockInvitations[invitationIndex].status = 'cancelled'
    return { error: null }
  }
}

// Mock Activity Service
export const mockActivityService = {
  // Get activity logs for a brand
  getActivityLogs: async (brandId: string, limit: number = 50): Promise<{ activities: ActivityLog[]; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const activities = mockActivityLogs
      .filter(activity => activity.brandId === brandId)
      .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
      .slice(0, limit)
    
    return { activities, error: null }
  },

  // Add activity log
  addActivity: (brandId: string, action: string, details: string, metadata?: any): void => {
    if (!mockUser) return

    const activity: ActivityLog = {
      id: 'activity-' + Date.now(),
      userId: mockUser.id,
      userName: mockUser.user_metadata?.full_name || mockUser.email,
      brandId,
      action,
      details,
      metadata,
      timestamp: new Date().toISOString()
    }
    
    mockActivityLogs.push(activity)
  }
}

// Mock Comment Service
export const mockCommentService = {
  // Get comments for a target
  getComments: async (brandId: string, targetType: 'brand' | 'toolbox_item', targetId: string): Promise<{ comments: Comment[]; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const comments = mockComments
      .filter(comment => 
        comment.brandId === brandId && 
        comment.targetType === targetType && 
        comment.targetId === targetId
      )
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    
    return { comments, error: null }
  },

  // Add comment
  addComment: async (brandId: string, targetType: 'brand' | 'toolbox_item', targetId: string, content: string, parentId?: string): Promise<{ comment: Comment | null; error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 300))
    
    if (!mockUser) {
      return { comment: null, error: 'User not authenticated' }
    }

    const comment: Comment = {
      id: 'comment-' + Date.now(),
      userId: mockUser.id,
      userName: mockUser.user_metadata?.full_name || mockUser.email,
      brandId,
      targetType,
      targetId,
      content,
      timestamp: new Date().toISOString(),
      parentId,
      resolved: false
    }

    mockComments.push(comment)

    // Add activity log
    const activity: ActivityLog = {
      id: 'activity-' + Date.now(),
      userId: mockUser.id,
      userName: mockUser.user_metadata?.full_name || mockUser.email,
      brandId,
      action: 'commented',
      details: `Added comment on ${targetType === 'toolbox_item' ? targetId : 'brand'}`,
      timestamp: new Date().toISOString()
    }
    mockActivityLogs.push(activity)

    return { comment, error: null }
  },

  // Resolve comment
  resolveComment: async (commentId: string): Promise<{ error: string | null }> => {
    await new Promise(resolve => setTimeout(resolve, 200))
    
    const commentIndex = mockComments.findIndex(comment => comment.id === commentId)
    if (commentIndex === -1) {
      return { error: 'Comment not found' }
    }

    mockComments[commentIndex].resolved = true
    return { error: null }
  }
}

// Export the type for external use
export type { User as UserType, Brand as BrandType, Profile as ProfileType, TeamMember, TeamInvitation, ActivityLog, Comment }